import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Table, TableName } from '@hero/components/molecules';
import { useIsAdminOrganization } from '@hero/hooks';
import { ROUTE_PATHS } from '@hero/routes';
import { useGetOrganizationUsers } from '@hero/services/organization-service/hooks';

import { ActionCell } from './ActionCell';
import { Filter } from './Filter';
import { SecondHead } from './SecondHead';
import { StatusCell } from './StatusCell';

function OrganizationUsersTable() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isOrganizationAdmin = useIsAdminOrganization();

  const [globalFilters, setGlobalFilters] = useState({
    searchTerm: '',
    filters: {
      role: [],
      status: []
    }
  });
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });

  const { data: organizationUsers, isLoading } = useGetOrganizationUsers({
    filters: {
      searchTerm: globalFilters.searchTerm,
      roles: globalFilters.filters?.role.map(filter => filter.value),
      statuses: globalFilters.filters?.status.map(filter => filter.value)
    },
    pagination: {
      page: pageIndex + 1,
      limit: pageSize
    }
  });

  function firstNameCell({
    row: {
      original: { code, firstName, lastName, role }
    }
  }) {
    if (!firstName || !lastName) {
      return null;
    }

    return (
      <TableName
        onClick={
          role !== isOrganizationAdmin
            ? () => {
                navigate(ROUTE_PATHS.SETTINGS_USERS_DETAIL(code));
              }
            : undefined
        }
        firstName={firstName}
        lastName={lastName}
      />
    );
  }

  const columns = useMemo(
    () => [
      {
        header: t('name'),
        cell: firstNameCell
      },
      {
        id: 'email',
        header: t('email'),
        accessorKey: 'email'
      },
      {
        id: 'role',
        header: t('role'),
        accessorFn: val => {
          return t(val.role, { count: 1 });
        }
      },
      {
        header: t('status'),
        accessorKey: 'status',
        cell: StatusCell
      },
      {
        id: 'actions',
        header: '',
        size: 50,
        cell: ActionCell
      }
    ],
    []
  );

  return (
    <Table
      data={organizationUsers?.docs || []}
      columns={columns}
      globalFilters={globalFilters}
      pagination={{
        pageIndex,
        pageSize,
        total: organizationUsers?.pagination?.total
      }}
      manualPagination
      loading={isLoading}
      setGlobalFilters={getGlobalFilters => {
        const newGlobalFilters = getGlobalFilters(globalFilters);
        setGlobalFilters(newGlobalFilters);
      }}
      setPagination={setPagination}
      filterOptions={globalFilters.filters}
      paginationProps={{
        perPageSelection: true
      }}
      pageCount={organizationUsers?.pagination?.pages}
      enabled={{ filter: true, header: true, perPageSelection: true }}
      rowHeight="52px"
      translations={{
        filteredWithText: t('filteredUserWithText', {
          count: organizationUsers?.pagination?.total || '0'
        })
      }}
      filterProps={{
        placeholder: t('filterByType', { type: t('role') }),
        searchPlaceholder: t('searchRoleOnFilterPlaceholder')
      }}
      components={{
        SecondHead,
        Filter
      }}
    />
  );
}

export { OrganizationUsersTable };
